import { useState } from "react";
import { CheckCircle2, CreditCard, Mail } from "lucide-react";
import { Link } from "react-router-dom";

import { ApiError, fetchJson } from "@/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { BILLING_KEY } from "./api";
import { DisputeDialog } from "./DisputeDialog";
import { formatDate, formatMoneyCents } from "./format";

// Phase 4b ar-and-dunning FR-19 / FR-20 + Phase 4l polish. Customer
// billing summary card (billing email + card on file + outstanding
// total) above a per-invoice unpaid list with Pay-now. The portal-
// wide delinquency banner is owned by PortalLayout; this page does
// not duplicate it.

interface BillingPayload {
  delinquent: boolean;
  outstanding_total_cents: number;
  billing_email: string;
  default_card: BillingCard | null;
  unpaid: BillingInvoice[];
  paid: BillingInvoice[];
}

interface BillingCard {
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

interface BillingInvoice {
  id: number;
  invoice_number: string;
  order_number: string;
  received_at: string | null;
  total_cents: number;
  outstanding_balance_cents: number;
  days_past_due: number;
  status: string;
  paid_at: string | null;
  dispute_window_open: boolean;
  dispute_window_ends_at: string | null;
  open_dispute: { id: number; reason: string; status: string } | null;
}

function useBilling() {
  return useQuery({
    queryKey: BILLING_KEY,
    queryFn: async () => fetchJson<BillingPayload>("/portal/account/billing.json")
  });
}

function usePayInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) =>
      fetchJson<{ ok: boolean; invoice_id: number; paid_at: string }>(
        `/portal/account/invoices/${id}/pay.json`,
        { method: "POST" }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: BILLING_KEY });
      qc.invalidateQueries({ queryKey: ["portal", "dashboard"] });
    }
  });
}

export function BillingPage(): JSX.Element {
  const q = useBilling();
  const [error, setError] = useState<string | null>(null);
  const [disputeFor, setDisputeFor] = useState<BillingInvoice | null>(null);
  const pay = usePayInvoice();

  if (q.isLoading) return <div className="p-4 text-sm text-zinc-600">Loading…</div>;
  if (!q.data) {
    return <div className="p-4 text-sm text-red-700">Couldn&rsquo;t load billing.</div>;
  }

  const data = q.data;

  function handlePay(id: number): void {
    setError(null);
    pay.mutate(id, {
      onError: (e) => {
        if (e instanceof ApiError) {
          const body = e.body as { reason?: string } | null;
          setError(reasonMessage(body?.reason));
        } else {
          setError(e.message);
        }
      }
    });
  }

  return (
    <section className="space-y-6">
      <header className="space-y-1.5">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-red">Account</p>
        <h1 className="font-serif text-2xl font-bold italic tracking-tight text-brand-black md:text-3xl">
          Billing
        </h1>
        <span aria-hidden="true" className="inline-block h-0.5 w-10 bg-brand-red" />
      </header>

      <BillingSummary data={data} />

      {error ? (
        <p className="rounded border border-red-600 bg-red-50 p-2 text-sm text-red-700"
           data-testid="billing-error">
          {error}
        </p>
      ) : null}

      <section>
        <h2 className="text-base font-semibold">Outstanding invoices</h2>
        {data.unpaid.length === 0 ? (
          <CaughtUpEmptyState />
        ) : (
          <ul className="mt-3 space-y-2">
            {data.unpaid.map((inv) => (
              <li key={inv.id} className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm"
                  data-testid={`unpaid-${inv.id}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <div>
                    <p className="font-medium text-brand-black">{inv.invoice_number}</p>
                    <p className="text-xs text-zinc-600">
                      {inv.received_at ? formatDate(inv.received_at) : "—"} ·
                      {" "}{inv.days_past_due} day{inv.days_past_due === 1 ? "" : "s"} past due
                    </p>
                  </div>
                  <p className="text-base font-semibold">
                    {formatMoneyCents(inv.outstanding_balance_cents)}
                  </p>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button type="button"
                          onClick={() => handlePay(inv.id)}
                          disabled={pay.isPending}
                          className="min-h-[44px] rounded-md bg-brand-red px-4 py-2 text-sm font-semibold text-brand-white transition hover:bg-brand-red-dark disabled:opacity-40"
                          data-testid={`pay-${inv.id}`}>
                    {pay.isPending ? "Charging…" : "Pay now"}
                  </button>
                  <DisputeAction invoice={inv} onOpen={() => setDisputeFor(inv)} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {data.paid.length > 0 ? (
        <section>
          <h2 className="text-base font-semibold">Paid invoices</h2>
          <ul className="mt-3 space-y-1 text-sm text-zinc-700">
            {data.paid.map((inv) => (
              <li key={inv.id} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b border-zinc-100 py-2 last:border-b-0"
                  data-testid={`paid-${inv.id}`}>
                <span>
                  {inv.invoice_number} · {inv.received_at ? formatDate(inv.received_at) : "—"}
                </span>
                <span className="flex items-center gap-3">
                  <span>{formatMoneyCents(inv.total_cents)} · Paid</span>
                  <DisputeAction invoice={inv} onOpen={() => setDisputeFor(inv)} compact />
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <BillingHelpFooter />

      {disputeFor ? (
        <DisputeDialog
          invoice={disputeFor}
          onClose={() => setDisputeFor(null)}
        />
      ) : null}
    </section>
  );
}

// Renders one of three states for an invoice row's dispute slot:
//   * an open dispute pill (read-only) when one exists,
//   * a "Dispute charge" button when the 15-day window is open,
//   * a muted "Dispute window closed" note otherwise.
// `compact` swaps the prominent outlined button for a small text link
// — used in the paid-invoice list where the line is denser.
function DisputeAction({ invoice, onOpen, compact = false }: {
  invoice: BillingInvoice;
  onOpen: () => void;
  compact?: boolean;
}): JSX.Element | null {
  if (invoice.open_dispute) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-flag-navy bg-flag-navy/5 px-2.5 py-1 text-xs font-semibold text-flag-navy"
            data-testid={`dispute-pill-${invoice.id}`}>
        <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-flag-navy" />
        Dispute open · #{invoice.open_dispute.id}
      </span>
    );
  }

  if (!invoice.dispute_window_open) {
    if (compact) return null;
    return (
      <span className="text-xs text-zinc-500" data-testid={`dispute-closed-${invoice.id}`}>
        Dispute window closed
      </span>
    );
  }

  if (compact) {
    return (
      <button type="button"
              onClick={onOpen}
              className="text-xs font-semibold text-brand-red underline-offset-2 transition hover:underline"
              data-testid={`dispute-open-${invoice.id}`}>
        Dispute charge
      </button>
    );
  }

  return (
    <button type="button"
            onClick={onOpen}
            className="min-h-[44px] rounded-md border border-brand-red px-4 py-2 text-sm font-semibold text-brand-red transition hover:bg-brand-red hover:text-brand-white"
            data-testid={`dispute-${invoice.id}`}>
      Dispute charge
    </button>
  );
}

// Honest answer to the audit's "Net 30 upgrade CTA" ask — JDR doesn't
// expose terms in the customer payload (or self-serve them); the right
// behavior is to invite the call instead of faking a button. Phone
// number matches the rest of the site (footer, empty cart, etc.).
function BillingHelpFooter(): JSX.Element {
  return (
    <p className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-700"
       data-testid="billing-help-footer">
      Need invoicing terms, a Net 30 setup, or a statement for your accountant?{" "}
      <a href="tel:+13034354328"
         className="font-semibold text-brand-red transition hover:underline">
        Call us at (303) 435-4328
      </a>
      .
    </p>
  );
}

function BillingSummary({ data }: { data: BillingPayload }): JSX.Element {
  // The portal-wide delinquency banner lives in PortalLayout (rendered
  // when `dashboard.delinquent` is true); we surface the outstanding
  // total inside this card so the customer sees the actionable number
  // without a duplicate red box. Guarded on `> 0` to handle the race
  // where the delinquent flag hasn't cleared yet but pay-now zeroed
  // the balance.
  const outstanding = data.delinquent && data.outstanding_total_cents > 0;
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm"
         data-testid="billing-summary">
      {outstanding ? (
        <div className="mb-4 flex items-baseline justify-between gap-3 border-b border-zinc-100 pb-4"
             data-testid="billing-outstanding-total">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-red">Outstanding balance</p>
            <p className="font-serif text-2xl font-bold italic text-brand-black">
              {formatMoneyCents(data.outstanding_total_cents)}
            </p>
          </div>
          <p className="text-xs text-zinc-600">
            Pay below to clear your account.
          </p>
        </div>
      ) : null}

      <dl className="grid gap-4 sm:grid-cols-2">
        <div className="flex items-start gap-3">
          <Mail aria-hidden="true" className="mt-0.5 h-5 w-5 text-flag-navy" />
          <div className="min-w-0">
            <dt className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Billing email</dt>
            <dd className="truncate text-sm text-brand-black">{data.billing_email}</dd>
            <dd className="mt-0.5 text-xs text-zinc-500">Receipts and invoices go here.</dd>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <CreditCard aria-hidden="true" className="mt-0.5 h-5 w-5 text-flag-navy" />
          <div className="min-w-0">
            <dt className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Card on file</dt>
            {data.default_card ? (
              <>
                <dd className="text-sm text-brand-black" data-testid="billing-card-summary">
                  {data.default_card.brand} ····{data.default_card.last4}
                </dd>
                <dd className="mt-0.5 text-xs text-zinc-500">
                  Expires {String(data.default_card.exp_month).padStart(2, "0")}/{String(data.default_card.exp_year).slice(-2)} ·{" "}
                  <Link to="/portal/profile#payment"
                        className="font-semibold text-brand-red transition hover:underline">
                    Manage
                  </Link>
                </dd>
              </>
            ) : (
              <>
                <dd className="text-sm text-zinc-700" data-testid="billing-card-summary">No card on file.</dd>
                <dd className="mt-0.5 text-xs">
                  <Link to="/portal/profile#payment"
                        className="font-semibold text-brand-red transition hover:underline">
                    Add a payment method →
                  </Link>
                </dd>
              </>
            )}
          </div>
        </div>
      </dl>
    </div>
  );
}

function CaughtUpEmptyState(): JSX.Element {
  return (
    <div className="mt-3 flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4"
         data-testid="billing-caught-up">
      <CheckCircle2 aria-hidden="true" className="h-6 w-6 shrink-0 text-emerald-600" />
      <div>
        <p className="text-sm font-semibold text-emerald-900">You&rsquo;re all caught up.</p>
        <p className="text-xs text-emerald-800">
          We&rsquo;ll email you here as soon as a new invoice is ready.
        </p>
      </div>
    </div>
  );
}

function reasonMessage(reason?: string): string {
  switch (reason) {
    case "no_payment_source":
    case "processor_unconfigured":
      // `processor_unconfigured` is a separate audit-log signal for
      // ops but the customer copy is identical: ops needs to set up
      // the order's processor before pay-now can charge.
      return "We don't have a card on file for this order. Please call us at (303) 435-4328.";
    case "capture_failed":
      // Adapter returns `capture_failed` for any upstream failure
      // (decline, timeout, gateway error). Don't claim "declined"
      // — softer wording covers all cases.
      return "We couldn't charge your card. Please try again, or call us at (303) 435-4328.";
    case "already_paid":
      return "This invoice is already paid.";
    case "written_off":
      return "This invoice has been written off.";
    default:
      return "We couldn't process the payment. Please try again or call us.";
  }
}
